import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, CircularProgress,
  Chip, Avatar, Divider, Tooltip, IconButton, Button,
  TextField, Select, MenuItem, InputAdornment, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack, LinearProgress, Checkbox, FormControlLabel, Fade, Grow, Zoom,
  Paper, Tabs, Tab
} from '@mui/material';
import {
  MenuBook as SyllabusIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ContentCopy as CloneIcon, UploadFile as ImportIcon, Download as DownloadIcon,
  Assessment as ReportIcon, Close as CloseIcon, Search as SearchIcon,
  Refresh as RefreshIcon, NavigateBefore as PrevIcon, NavigateNext as NextIcon,
  CheckCircle as CheckIcon, Warning as WarnIcon, PlaylistAddCheck as TrackIcon,
  LocationOn as LocationOnIcon, CloudUpload as CloudUploadIcon,
  SwapHorizontalCircle as SwapIcon, AutoAwesome as SparkIcon,
  BarChart as BarChartIcon, PieChart as PieChartIcon, TrendingUp as TrendingUpIcon,
  DonutLarge as DonutLargeIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, LabelList, RadialBarChart, RadialBar, AreaChart, Area
} from 'recharts';

/* ============================================================================
   DESIGN SYSTEM
   Theme: "Circular Syllabus" — the ring is the motif. Coverage, the core idea
   of this module, is inherently a completion metric, so progress is shown as
   an arc/ring everywhere (hero ring, row rings, radial chart) instead of only
   flat bars. Display type: Sora (headings) · Body/UI: Inter.
   ============================================================================ */
const T = {
  ink: '#0F1923', slate: '#1E2D3D', indigo: '#2563EB', indigoD: '#1D4ED8',
  teal: '#0D9488', emerald: '#059669', amber: '#D97706', rose: '#E11D48',
  violet: '#7C3AED', sky: '#0284C7', skyL: '#E0F2FE', border: '#E2E8F0',
  surface: '#F3F5FA', card: '#FFFFFF', muted: '#64748B', faint: '#94A3B8', ghost: '#F8FAFC',
};
const RING_GRADIENT = ['#2563EB', '#7C3AED', '#0EA5E9'];
const FONT_DISPLAY = "'Sora', 'Segoe UI', sans-serif";
const FONT_BODY = "'Inter', 'Segoe UI', sans-serif";

const ROW_OPTIONS = [5, 10, 15, 25];

// API Base URL (Update with your actual base URL)
const BASE = 'https://wsmsv1.aidigitalsec.com/api';
const tok = () => sessionStorage.getItem('token');
const authH = () => ({ Authorization: `${tok()}` });
const postH = () => ({ 'Content-Type': 'application/json', ...authH() });

const safeJ = async (r) => {
  try { return await r.json(); } catch { return null; }
};

// API Methods — every endpoint on this screen is POST-only on the backend,
// including "read" endpoints like GetById (no native GET verb is used anywhere).
const apiPost = (path, body) => fetch(`${BASE}${path}`, {
  method: 'POST',
  headers: postH(),
  body: JSON.stringify(body ?? {})
});

const apiPostForm = (path, formData) => fetch(`${BASE}${path}`, {
  method: 'POST',
  headers: authH(),
  body: formData
});

const apiGet = (path) => fetch(`${BASE}${path}`, {
  method: 'GET',
  headers: authH()
});

const apiPut = (path, body) => fetch(`${BASE}${path}`, {
  method: 'PUT',
  headers: postH(),
  body: JSON.stringify(body ?? {})
});

const apiDelete = (path) => fetch(`${BASE}${path}`, {
  method: 'DELETE',
  headers: authH()
});

// Master Data Fetching
const fetchClasses = () => apiPost('/Enquiry/ddlclassName', {}).then(safeJ);
const fetchSubjects = () => apiPost('/SubjectMaster/GetSubject', {}).then(safeJ);
const fetchFinancialYears = () => apiPost('/ClassPromotion/GetFinancialYear', {}).then(safeJ);
const fetchMonths = () => apiPost('/Fine/GetMonthList', {}).then(safeJ);
const fetchSectionsByClass = (classId) => classId
  ? apiPost('/ClassPromotion/ddlSection_clsId', { teacherId: 0, classId }).then(safeJ)
  : Promise.resolve([]);

// Field accessors
const cId = (c) => c.classId; const cName = (c) => c.className;
const secId = (s) => s.sectionId; const secName = (s) => s.sectionName;
const subId = (s) => s.subjectId; const subName = (s) => s.subjectName;
const moId = (m) => m.monthId; const moName = (m) => m.month;
const fyId = (f) => f.financialYearID; const fyName = (f) => f.finanacialYear;

// Current Financial Year
const currentFYLabel = () => {
  const now = new Date();
  const y = now.getFullYear();
  return now.getMonth() >= 3 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

// Date Formatter
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric'
}) : '—';

// Unique ID Generator
const uid = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Status Mapping
const STATUS_MAP = { P: 'Pending', C: 'Covered', IP: 'In Progress' };

// Coverage State Styling
const coverageState = (pct) => {
  if (pct >= 100) return { label: 'Completed', color: T.emerald, bg: '#D1FAE5', border: '#6EE7B7' };
  if (pct > 0) return { label: 'In Progress', color: T.sky, bg: T.skyL, border: '#7DD3FC' };
  return { label: 'Not Covered', color: T.muted, bg: '#F1F5F9', border: '#E2E8F0' };
};

/* ---------- inject webfonts once ---------- */
function useWebFonts() {
  useEffect(() => {
    if (document.getElementById('circular-syllabus-fonts')) return;
    const link = document.createElement('link');
    link.id = 'circular-syllabus-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

function useDisableStickyMenuHover() {
  useEffect(() => {
    if (document.getElementById('no-sticky-menu-hover')) return;
    const style = document.createElement('style');
    style.id = 'no-sticky-menu-hover';
    style.innerHTML = `
      @media (hover: none) {
        .MuiMenuItem-root:hover,
        .MuiMenuItem-root.Mui-focusVisible {
          background-color: transparent !important;
        }
        .MuiMenuItem-root.Mui-selected {
          background-color: rgba(37,99,235,0.08) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

// Count-Up Animation Hook
function useCountUp(value, duration = 700) {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const from = display;
    const to = Number(value) || 0;
    cancelAnimationFrame(raf.current);
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);
  return display;
}

/* ---------- Circular ring progress (the signature motif) ---------- */
const RingProgress = ({ pct = 0, size = 40, stroke = 4.5, colorFrom = RING_GRADIENT[0], colorTo = RING_GRADIENT[1], label, gradId }) => {
  const animated = useCountUp(pct, 900);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, animated)) / 100) * c;
  const gid = gradId || `ring-${Math.round(pct)}-${size}`;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colorFrom} />
            <stop offset="100%" stopColor={colorTo} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF2F7" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`url(#${gid})`} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset .3s ease' }}
        />
      </svg>
      {label !== false && (
        <Typography sx={{
          position: 'absolute', fontWeight: 800, fontFamily: FONT_DISPLAY,
          fontSize: size < 50 ? '0.6rem' : '0.95rem', color: T.ink
        }}>
          {Math.round(animated)}%
        </Typography>
      )}
    </Box>
  );
};

// Reusable Components
const TH = ({ children, align = 'left', width }) => (
  <TableCell align={align} sx={{
    bgcolor: '#EEF2F9', fontWeight: 700, fontSize: '0.72rem', color: T.slate,
    py: 1.4, letterSpacing: 0.4, textTransform: 'uppercase', fontFamily: FONT_BODY,
    borderBottom: `2px solid ${T.border}`, ...(width ? { width } : {}),
  }}>{children}</TableCell>
);

const StatusPill = ({ status }) => {
  const map = {
    Completed: { bg: '#D1FAE5', color: T.emerald, dot: '#10B981' },
    'In Progress': { bg: T.skyL, color: T.sky, dot: '#0EA5E9' },
    'Not Covered': { bg: '#F1F5F9', color: T.muted, dot: '#94A3B8' },
    Covered: { bg: '#D1FAE5', color: T.emerald, dot: '#10B981' },
    Pending: { bg: '#FEF3C7', color: T.amber, dot: '#F59E0B' },
  };
  const s = map[status] || { bg: '#F1F5F9', color: T.muted, dot: T.faint };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: s.bg, color: s.color,
      fontWeight: 700, fontSize: '0.68rem', px: 1, py: 0.35, borderRadius: '20px', lineHeight: 1.5, whiteSpace: 'nowrap'
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: s.dot, flexShrink: 0 }} />
      {status || '—'}
    </Box>
  );
};

const SearchBar = ({ value, onChange, placeholder = 'Search…', width = 260 }) => (
  <TextField
    size="small" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
    InputProps={{
      startAdornment: <InputAdornment position="start">
        <SearchIcon sx={{ fontSize: 15, color: T.faint }} />
      </InputAdornment>
    }}
    sx={{
      width, '& .MuiOutlinedInput-root': {
        borderRadius: '10px', fontSize: '0.83rem', bgcolor: T.ghost, transition: 'all .2s',
        '&:hover fieldset': { borderColor: T.indigo },
        '&.Mui-focused fieldset': { borderColor: T.indigo, borderWidth: 1.5 },
      }
    }}
  />
);

const Pager = ({ total, page, rowsPerPage, onPage, onRows }) => {
  const pages = Math.max(Math.ceil(total / rowsPerPage), 1);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5,
      borderTop: `1px solid ${T.border}`, flexWrap: 'wrap', gap: 1, bgcolor: T.ghost
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>Rows per page:</Typography>
        <Select
          value={rowsPerPage} onChange={e => { onRows(Number(e.target.value)); onPage(1); }}
          size="small" sx={{ fontSize: '0.78rem', height: 28, minWidth: 60, borderRadius: '8px', bgcolor: T.card }}
        >
          {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
          {total === 0 ? '0' : `${Math.min((page - 1) * rowsPerPage + 1, total)}–${Math.min(page * rowsPerPage, total)}`} of {total}
        </Typography>
        <IconButton size="small" onClick={() => onPage(p => p - 1)} disabled={page <= 1} sx={{ '&:not(:disabled)': { color: T.indigo } }}>
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onPage(p => p + 1)} disabled={page >= pages} sx={{ '&:not(:disabled)': { color: T.indigo } }}>
          <NextIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const shimmerSx = {
  background: 'linear-gradient(90deg, #EEF2F7 25%, #F8FAFC 37%, #EEF2F7 63%)',
  backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite',
  '@keyframes shimmer': { '0%': { backgroundPosition: '100% 50%' }, '100%': { backgroundPosition: '0 50%' } },
  borderRadius: '6px',
};

const SkeletonRow = () => (
  <TableRow>
    {Array.from({ length: 9 }).map((_, i) => (
      <TableCell key={i}><Box sx={{ height: 16, width: i === 0 ? 24 : '80%', ...shimmerSx }} /></TableCell>
    ))}
  </TableRow>
);

const KpiChip = ({ label, value, suffix = '', bg, border, color, delay = 0, icon }) => {
  const n = useCountUp(value);
  return (
    <Grow in timeout={420 + delay}>
      <Box sx={{
        bgcolor: bg, border: `1px solid ${border}`, borderRadius: '14px', px: 2, py: 1.1, textAlign: 'center', minWidth: 84,
        position: 'relative', overflow: 'hidden', transition: 'transform .25s, box-shadow .25s', backdropFilter: 'blur(6px)',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 10px 24px ${border}` },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {icon}
          <Typography sx={{ fontWeight: 800, color, fontSize: '1.4rem', lineHeight: 1, fontFamily: FONT_DISPLAY }}>
            {Math.round(n)}{suffix}
          </Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem', fontWeight: 600, mt: 0.3 }}>{label}</Typography>
      </Box>
    </Grow>
  );
};

/* ---------- decorative orbiting rings for the header (pure CSS, no assets) ---------- */
const OrbitDecoration = () => (
  <Box sx={{ position: 'absolute', top: -70, right: -40, width: 300, height: 300, pointerEvents: 'none', opacity: 0.5 }}>
    {[220, 160, 100].map((d, i) => (
      <Box key={d} sx={{
        position: 'absolute', top: '50%', left: '50%', width: d, height: d,
        border: '1.5px dashed rgba(255,255,255,0.18)', borderRadius: '50%',
        transform: 'translate(-50%,-50%)',
        animation: `spin${i} ${18 + i * 8}s linear infinite`,
        [`@keyframes spin${i}`]: {
          '0%': { transform: 'translate(-50%,-50%) rotate(0deg)' },
          '100%': { transform: 'translate(-50%,-50%) rotate(360deg)' },
        },
      }}>
        <Box sx={{
          position: 'absolute', top: -3, left: '50%', width: 6, height: 6, borderRadius: '50%',
          bgcolor: i === 0 ? '#93C5FD' : i === 1 ? '#7DD3FC' : '#C4B5FD', boxShadow: '0 0 10px rgba(147,197,253,0.9)'
        }} />
      </Box>
    ))}
  </Box>
);

// Analytics Components
const CoverageByClassChart = ({ data }) => {
  return (
    <Card sx={{ borderRadius: '18px', boxShadow: '0 4px 24px rgba(15,25,35,0.08)', border: `1px solid ${T.border}`, p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ bgcolor: '#EFF6FF', borderRadius: '10px', p: 0.7, display: 'flex' }}>
          <BarChartIcon sx={{ fontSize: 16, color: T.indigo }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY }}>Coverage by Class</Typography>
      </Box>
      {data.length === 0 ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.faint, fontSize: '0.82rem' }}>
          No coverage data yet for the current filters.
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data}>
            <defs>
              <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.indigo} stopOpacity={0.95} />
                <stop offset="100%" stopColor={T.violet} stopOpacity={0.75} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
            <XAxis dataKey="className" stroke={T.muted} fontSize={12} />
            <YAxis stroke={T.muted} fontSize={12} />
            <RechartsTooltip
              contentStyle={{ borderRadius: '10px', border: `1px solid ${T.border}`, fontSize: '0.8rem' }}
              formatter={(value) => [`${value.toFixed ? value.toFixed(1) : value}%`, 'Coverage']}
            />
            <Bar dataKey="coverage" fill="url(#barGrad)" radius={[6, 6, 0, 0]} animationDuration={900} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

const StatusPieChart = ({ data }) => {
  const COLORS = [T.emerald, T.sky, T.amber];
  return (
    <Card sx={{ borderRadius: '18px', boxShadow: '0 4px 24px rgba(15,25,35,0.08)', border: `1px solid ${T.border}`, p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ bgcolor: '#F0FDFA', borderRadius: '10px', p: 0.7, display: 'flex' }}>
          <PieChartIcon sx={{ fontSize: 16, color: T.teal }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY }}>Topic Status Distribution</Typography>
      </Box>
      {data.length === 0 ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.faint, fontSize: '0.82rem' }}>
          No topics tracked yet for the current filters.
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={80}
              paddingAngle={3} dataKey="value" animationDuration={900}
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
              ))}
            </Pie>
            <RechartsTooltip contentStyle={{ borderRadius: '10px', border: `1px solid ${T.border}`, fontSize: '0.8rem' }} />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}; 

const OverallCompletionCard = ({ pct, classCount }) => (
  <Card sx={{
    borderRadius: '18px', boxShadow: '0 4px 24px rgba(15,25,35,0.08)', border: `1px solid ${T.border}`,
    p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5
  }}>
    <Typography sx={{ fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY, alignSelf: 'flex-start' }}>
      Overall Completion
    </Typography>
    <RingProgress pct={pct} size={150} stroke={12} colorFrom={RING_GRADIENT[0]} colorTo={RING_GRADIENT[1]} />
    <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.75rem', textAlign: 'center' }}>
      Averaged across {classCount || 0} class{classCount === 1 ? '' : 'es'} with active syllabus
    </Typography>
  </Card>
);

// Main Component
export default function ManageSyllabus() {
  useWebFonts();
  useDisableStickyMenuHover();
const [cloneConfirmOpen, setCloneConfirmOpen] = useState(false);
  // Analytics tab filters (independent from Syllabus List filters)
const [analyticsList, setAnalyticsList] = useState([]);
const [analyticsLoading, setAnalyticsLoading] = useState(false);
const [filtefSubjects, setFiltefSubjects] = useState([]);
const [formSubjects, setFormSubjects] = useState([]);
const [cloneSubjects, setCloneSubjects] = useState([]);
  // Coverage Report tab filters (independent from Syllabus List filters)

  const [expandedRemarks, setExpandedRemarks] = useState({});
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Master Data
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [fys, setFys] = useState([]);
  const [months, setMonths] = useState([]);
  const [mastersLoaded, setMastersLoaded] = useState(false);

  // Cascading Sections
  const [filtefSections, setFiltefSections] = useState([]);
  const [formSections, setFormSections] = useState([]);
  const [cloneSections, setCloneSections] = useState([]);

  // Filters
  const [fClass, setFClass] = useState('');
  const [fSection, setFSection] = useState('');
  const [fSubject, setFSubject] = useState('');
  const [fFy, setFFy] = useState('');
  const [fMonth, setFMonth] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);

  // Data
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formLoadingDetail, setFormLoadingDetail] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const emptyForm = {
    SyllabusId: null, ClassId: '', SectionId: '', SubjectId: '', FinancialYearId: '', MonthId: '',
    ThemeNo: 1, themeName: '', themeDescription: '',
    Topics: [{ _id: uid(), TopicSequence: 1, TopicName: '' }],
  };
  const [form, setForm] = useState(emptyForm);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRec, setDeleteRec] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [trackOpen, setTrackOpen] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackSyllabus, setTrackSyllabus] = useState(null);
  const [trackTopics, setTrackTopics] = useState([]);
  const [trackSaving, setTrackSaving] = useState(false);

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneForm, setCloneForm] = useState({
    ClassId: '', SectionId: '', SubjectId: '', MonthId: '',
    FromFinancialYearId: '', ToFinancialYearId: '', CloneTopics: true, ResetTracking: true,
  });
  const [cloneClasses, setCloneClasses] = useState([]);

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [reportLoading, setReportLoading] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [exporting, setExporting] = useState(false);

  const [analyticsReloadKey, setAnalyticsReloadKey] = useState(0);
const [reportReloadKey, setReportReloadKey] = useState(0);

  // Analytics Data
  const [analyticsData, setAnalyticsData] = useState({
    coverageByClass: [],
    statusDistribution: [],
  });

  // Tab State
  const [activeTab, setActiveTab] = useState(0);

useEffect(() => {
  fetchSubjectsByClass(fClass).then(d => {
    setFiltefSubjects(Array.isArray(d) ? d : []);
    setFSubject('');
  });
}, [fClass]);

useEffect(() => {
  if (formMode === 'create') fetchSubjectsByClass(form.ClassId).then(d => {
    setFormSubjects(Array.isArray(d) ? d.filter(s => s.status === '1') : []);
  });
}, [form.ClassId, formMode]);

useEffect(() => {
  fetchSubjectsByClass(cloneForm.ClassId).then(d => {
    setCloneSubjects(Array.isArray(d) ? d.filter(s => s.status === '1') : []);
  });
}, [cloneForm.ClassId]);

  // Initialize
  useEffect(() => {
    const masterFlag = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(masterFlag);
    if (masterFlag) fetchBranches();
    else {
      const branchId = sessionStorage.getItem('branchId');
      if (branchId) setSelectedBranchId(Number(branchId));
    }
    loadMasters();
  }, []);

  const fetchBranches = async () => {
    try {
      const res = await apiPost('/Branch/GetBranch', {});
      const data = await safeJ(res);
      if (Array.isArray(data) && data.length > 0) {
        setBranches(data);
        setSelectedBranchId(data[0].branchId);
      }
    } catch (e) { console.error(e); }
  };

  const loadMasters = async () => {
    try {
      const [c, sub, fy, mo] = await Promise.all([
        fetchClasses(), fetchSubjects(), fetchFinancialYears(), fetchMonths()
      ]);
      const classArr = Array.isArray(c) ? c : [];
      const subArr = Array.isArray(sub) ? sub : [];
      const fyArr = Array.isArray(fy) ? fy : [];
      const moArr = Array.isArray(mo) ? mo : [];
      setClasses(classArr); setSubjects(subArr); setFys(fyArr); setMonths(moArr);
     const label = currentFYLabel();
      const match = fyArr.find(f => fyName(f) === label);
     const defaultFyId = (match ? fyId(match) : fyArr[fyArr.length - 1] && fyId(fyArr[fyArr.length - 1])) || '';
setFFy(defaultFyId);

      const currentMonthName = new Date().toLocaleString('en-US', { month: 'long' });
      const monthMatch = moArr.find(m => moName(m)?.toLowerCase() === currentMonthName.toLowerCase());
      if (monthMatch) setFMonth(moId(monthMatch));

    } catch (e) {
      console.error('Failed to load master data', e);
      showSnack('Failed to load dropdown data', 'error');
    } finally { setMastersLoaded(true); }
  };

  // Cascading Sections
  useEffect(() => {
    fetchSectionsByClass(fClass).then(d => {
      setFiltefSections(Array.isArray(d) ? d : []);
      setFSection('');
    });
  }, [fClass]);

  useEffect(() => {
    if (formMode === 'create') fetchSectionsByClass(form.ClassId).then(d => setFormSections(Array.isArray(d) ? d : []));
  }, [form.ClassId, formMode]);

  useEffect(() => {
    fetchSectionsByClass(cloneForm.ClassId).then(d => setCloneSections(Array.isArray(d) ? d : []));
  }, [cloneForm.ClassId]);

  const loadAnalytics = useCallback(async () => {
  if (!selectedBranchId) return;
    setAnalyticsReloadKey(k => k + 1); // add
  setAnalyticsLoading(true);
  try {
    const body = {
      ClassId: fClass || 0,
      SectionId: fSection || 0,
      SubjectId: fSubject || 0,
      MonthId: fMonth || 0,
      FinancialYearId: fFy || 0
    };
    const res = await apiPost(`/CircularSyllabus/GetByClassSubject?Branch_id=${selectedBranchId}`, body);
    const d = await safeJ(res);
    const syllabusList = Array.isArray(d?.syllabusList) ? d.syllabusList : [];
    setAnalyticsList(syllabusList);
    updateAnalyticsData(syllabusList);
  } catch (e) {
    console.error(e);
    showSnack('Failed to load analytics', 'error');
  } finally {
    setAnalyticsLoading(false);
  }
}, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy]);

  // Load Syllabus List
  const loadList = useCallback(async () => {
    if (!selectedBranchId) return;
    setLoading(true);
    try {
      const body = {
        ClassId: fClass || 0,
        SectionId: fSection || 0,
        SubjectId: fSubject || 0,
        MonthId: fMonth || 0,
        FinancialYearId: fFy || 0
      };
      const res = await apiPost(`/CircularSyllabus/GetByClassSubject?Branch_id=${selectedBranchId}`, body);
      const d = await safeJ(res);
      const syllabusList = Array.isArray(d?.syllabusList) ? d.syllabusList : [];
      setList(syllabusList);
      updateAnalyticsData(syllabusList);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load syllabus list', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy]);

  const updateAnalyticsData = (syllabusList) => {
    const classCoverage = classes.map(c => {
      const syllabusForClass = syllabusList.filter(s => s.classId === cId(c));
      const totalTopics = syllabusForClass.reduce((sum, s) => sum + (s.totalTopics || 0), 0);
      const coveredTopics = syllabusForClass.reduce((sum, s) => sum + (s.coveredTopics || 0), 0);
      const coverage = totalTopics ? (coveredTopics / totalTopics) * 100 : 0;
      return { className: cName(c), coverage };
    }).filter(c => c.coverage > 0);

    const totalPending = syllabusList.reduce((sum, s) => sum + (s.pendingTopics || 0), 0);
    const totalCovered = syllabusList.reduce((sum, s) => sum + (s.coveredTopics || 0), 0);
    const totalInProgress = syllabusList.reduce((sum, s) => sum + (s.totalTopics || 0) - (s.coveredTopics || 0) - (s.pendingTopics || 0), 0);

    const statusData = [
      { name: 'Covered', value: totalCovered },
      { name: 'In Progress', value: Math.max(0, totalInProgress) },
      { name: 'Pending', value: totalPending },
    ].filter(s => s.value > 0);

    setAnalyticsData({ coverageByClass: classCoverage, statusDistribution: statusData });
  };

  useEffect(() => {
    if (selectedBranchId && mastersLoaded) loadList();
  }, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy, mastersLoaded]);

 useEffect(() => {
  if (selectedBranchId && mastersLoaded) loadAnalytics();
}, [selectedBranchId, mastersLoaded, fClass, fSection, fSubject, fMonth, fFy]);

useEffect(() => {
  if (selectedBranchId && mastersLoaded) openReport();
}, [selectedBranchId, mastersLoaded, fClass, fSection, fSubject, fMonth, fFy]);

const analyticsKpis = useMemo(() => {
  const totalTopics = analyticsList.reduce((s, r) => s + (r.totalTopics ?? 0), 0);
  const coveredTopics = analyticsList.reduce((s, r) => s + (r.coveredTopics ?? 0), 0);
  const avgCoverage = totalTopics ? (coveredTopics / totalTopics) * 100 : 0;
  return { avgCoverage };
}, [analyticsList]);

  const filteredList = useMemo(() => list.filter(r =>
    !search ||
    (r.themeName ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (r.subjectName ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (r.className ?? '').toLowerCase().includes(search.toLowerCase())
  ), [list, search]);

  const pagedList = useMemo(() =>
    filteredList.slice((page - 1) * rows, page * rows),
    [filteredList, page, rows]
  );

  const kpis = useMemo(() => {
    const totalTopics = list.reduce((s, r) => s + (r.totalTopics ?? 0), 0);
    const coveredTopics = list.reduce((s, r) => s + (r.coveredTopics ?? 0), 0);
    const avgCoverage = totalTopics ? (coveredTopics / totalTopics) * 100 : 0;
    const completed = list.filter(r => (r.totalTopics ?? 0) > 0 && (r.coveredTopics ?? 0) === r.totalTopics).length;
    return {
      totalSyllabus: list.length,
      avgCoverage,
      completed,
      pendingTopics: totalTopics - coveredTopics
    };
  }, [list]);

  // Form Handlers
  const openCreate = () => {
    setForm({
      ...emptyForm,
      ClassId: fClass || '',
      SectionId: fSection || '',
      SubjectId: fSubject || '',
      FinancialYearId: fFy || '',
      MonthId: fMonth || '',
      Topics: [{ _id: uid(), TopicSequence: 1, TopicName: '' }]
    });
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = async (rec) => {
    setFormMode('edit');
    setFormOpen(true);
    setFormLoadingDetail(true);
    try {
      // NOTE: backend exposes GetById as a POST endpoint (no GET verb) — id is
      // still passed as a path segment, body is empty.
      const res = await apiGet(`/CircularSyllabus/GetById/${rec.syllabusId}?Branch_id=${selectedBranchId}`, {});
      const d = await safeJ(res);

      setForm({
        SyllabusId: d?.syllabusId ?? rec.syllabusId,
        ClassId: d?.classId ?? rec.classId,
        SectionId: d?.sectionId ?? rec.sectionId ?? '',
        SubjectId: d?.subjectId ?? rec.subjectId,
        FinancialYearId: d?.financialYearId ?? rec.financialYearId,
        MonthId: d?.monthId ?? rec.monthId,
        ThemeNo: d?.themeNo ?? rec.themeNo ?? 1,
        themeName: d?.themeName ?? rec.themeName ?? '',
        themeDescription: d?.themeDescription ?? rec.themeDescription ?? '',
        Topics: (d?.topics ?? []).length
          ? d.topics.map(t => ({
              _id: uid(),
              TrackingId: t.trackingId,
              TopicSequence: t.topicSequence,
              TopicName: t.topicName
            }))
          : [{ _id: uid(), TopicSequence: 1, TopicName: '' }],
        className: d?.className ?? rec.className,
        sectionName: d?.sectionName ?? rec.sectionName,
        subjectName: d?.subjectName ?? rec.subjectName,
        MonthName: d?.monthName ?? rec.monthName,
        FinancialYear: d?.financialYear ?? rec.financialYear,
      });
    } catch (e) {
      console.error(e);
      showSnack('Failed to load syllabus details', 'error');
    } finally {
      setFormLoadingDetail(false);
    }
  };

  const addTopicRow = () => setForm(f => ({
    ...f,
    Topics: [...f.Topics, { _id: uid(), TopicSequence: f.Topics.length + 1, TopicName: '' }]
  }));

  const removeTopicRow = (id) => setForm(f => {
    const next = f.Topics.filter(t => t._id !== id).map((t, i) => ({ ...t, TopicSequence: i + 1 }));
    return { ...f, Topics: next.length ? next : [{ _id: uid(), TopicSequence: 1, TopicName: '' }] };
  });

  const updateTopicName = (id, name) => setForm(f => ({
    ...f,
    Topics: f.Topics.map(t => t._id === id ? { ...t, TopicName: name } : t)
  }));

  const validateForm = () => {
    if (!form.ClassId || !form.SubjectId || !form.FinancialYearId || !form.MonthId) {
      showSnack('Please select class, subject, financial year and month', 'error');
      return false;
    }
    if (Number(form.ThemeNo) < 0) {
      showSnack('Theme No. cannot be negative', 'error');
      return false;
    }
    if (!form.themeName.trim()) {
      showSnack('Theme name is required', 'error');
      return false;
    }
    if (form.Topics.some(t => !t.TopicName.trim())) {
      showSnack('All topic names must be filled, or removed', 'error');
      return false;
    }
    return true;
  };

  const handleSaveForm = async () => {
    if (!validateForm()) return;
    setFormSaving(true);
    try {
      const topics = form.Topics.map(t => ({
        TopicSequence: t.TopicSequence,
        TopicName: t.TopicName.trim()
      }));
      let res;
      if (formMode === 'create') {
        const body = {
          ClassId: form.ClassId,
          SectionId: form.SectionId || 0,
          SubjectId: form.SubjectId,
          FinancialYearId: form.FinancialYearId,
          MonthId: form.MonthId,
          ThemeNo: Number(form.ThemeNo) || 1,
          themeName: form.themeName.trim(),
          themeDescription: form.themeDescription,
          Topics: topics
        };
        res = await apiPost(`/CircularSyllabus/Create?Branch_id=${selectedBranchId}`, body);
      } else {
        const body = {
          SyllabusId: form.SyllabusId,
          ThemeNo: Number(form.ThemeNo) || 1,
          themeName: form.themeName.trim(),
          themeDescription: form.themeDescription,
          Topics: topics
        };
        res = await apiPut(`/CircularSyllabus/Update?Branch_id=${selectedBranchId}`, body);
      }
      const d = await safeJ(res);
      if (res.ok) {
        showSnack(d?.Msg || `Syllabus ${formMode === 'create' ? 'created' : 'updated'} successfully`);
        setFormOpen(false);
        loadList();
      } else showSnack(d?.Msg || 'Save failed. Please try again.', 'error');
    } catch (e) {
      console.error(e);
      showSnack('Error saving syllabus', 'error');
    } finally { setFormSaving(false); }
  };

  // Delete Handler
  const handleDelete = async () => {
    if (!deleteRec) return;
    setDeleting(true);
    try {
      const res = await apiDelete(`/CircularSyllabus/Delete/${deleteRec.syllabusId}?Branch_id=${selectedBranchId}`, {});
      const d = await safeJ(res);
     if (res.ok && !(d?.msg ?? d?.Msg ?? '').toLowerCase().includes('failed')) {
  showSnack(d?.msg || d?.Msg || 'Syllabus deleted successfully');
  setDeleteOpen(false);
  loadList();
} else {
  showSnack(d?.msg || d?.Msg || 'Delete failed', 'error');
}
    } catch (e) {
      console.error(e);
      showSnack('Error deleting syllabus', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Topic Tracking (quick dialog — the full dedicated workflow lives on the
  // separate Topic Coverage page, see TopicCoverage.jsx)
  const openTrack = async (rec) => {
    setTrackSyllabus(rec);
    setTrackOpen(true);
    setTrackLoading(true);
    try {
      const res = await apiGet(`/CircularSyllabus/GetById/${rec.syllabusId}?Branch_id=${selectedBranchId}`, {});
      const d = await safeJ(res);

      setTrackTopics((d?.topics ?? []).map(t => ({
        TrackingId: t.trackingId,
        TopicSequence: t.topicSequence,
        TopicName: t.topicName,
        Status: t.status ?? 'P',
        Description: t.coveredDescription ?? '',
        CoveredByTeacherName: t.coveredByTeacherName,
        CoveredDate: t.coveredDate,
        CoveredDescription: t.coveredDescription ?? '',
        _dirty: false,
      })));
    } catch (e) {
      console.error(e);
      showSnack('Failed to load topics', 'error');
    } finally {
      setTrackLoading(false);
    }
  };

  const updateTrackTopic = (trackingId, patch) => setTrackTopics(ts =>
    ts.map(t => t.TrackingId === trackingId ? { ...t, ...patch, _dirty: true } : t)
  );

  const fetchUserGroups = () => apiPost('/CPanel/GetUserGroup', {}).then(safeJ);

  const fetchSubjectsByClass = (classId) => classId
  ? apiPost('/ClassMapping/GetSubjectMapping', { classId }).then(safeJ)
  : Promise.resolve([]);

const resolveTeacherName = async () => {
  let clientName = sessionStorage.getItem('clientName');

  // unwrap JSON-stringified value, e.g. '""' -> '', '"John"' -> 'John'
  try {
    const parsed = JSON.parse(clientName);
    if (typeof parsed === 'string') clientName = parsed;
  } catch {
    // raw string, leave as-is
  }

  clientName = (clientName || '').trim();
  if (clientName) return clientName;

  // clientName is blank -> fall back to userGroupId lookup
  const userGroupId = sessionStorage.getItem('userGroupId');
  if (!userGroupId) return 'Unknown Teacher';

  try {
    const groups = await fetchUserGroups();
    const match = Array.isArray(groups)
      ? groups.find(g => String(g.userGroupId) === String(userGroupId))
      : null;
    return match?.userGroupName || 'Unknown Teacher';
  } catch (e) {
    console.error('Failed to resolve user group name', e);
    return 'Unknown Teacher';
  }
};

const handleSaveTrack = async () => {
    const dirty = trackTopics.filter(t => t._dirty);
    if (!dirty.length) {
      setTrackOpen(false);
      return;
    }
    setTrackSaving(true);
    try {
      const teacherId = sessionStorage.getItem('employeeId') || 0;
      const teacherName = await resolveTeacherName();

      const body = {
        Topics: dirty.map(t => ({
          trackingId: t.TrackingId,
          status: t.Status,
          coveredby_TeacherId: teacherId,
          coveredby_TeacherName: teacherName,
          description: t.Description || ''
        }))
      };

      const res = await apiPost(`/CircularSyllabus/MarkCovered?Branch_id=${selectedBranchId}`, body);
      const d = await safeJ(res);

      if (res.ok) {
        showSnack(d?.Msg || `${d?.RowsAffected ?? dirty.length} topic(s) updated`);
        setTrackOpen(false);
        loadList();
      } else {
        showSnack(d?.Msg || 'Update failed', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnack('Error updating topics', 'error');
    } finally {
      setTrackSaving(false);
    }
  }; 

  // Clone Handler
  const openClone = () => {
    setCloneForm({
      ClassId: '',
      SectionId: '',
      SubjectId: '',
      MonthId: '',
      FromFinancialYearId: fFy || '',
      ToFinancialYearId: '',
      CloneTopics: true,
      ResetTracking: true
    });
    setCloneOpen(true);
  };

  useEffect(() => {
    if (!cloneOpen || !cloneForm.FromFinancialYearId || !selectedBranchId) return;
    (async () => {
      try {
        const res = await apiPost(`/CircularSyllabus/GetClassesWithSyllabus?Branch_id=${selectedBranchId}&financialYearId=${cloneForm.FromFinancialYearId}`, {});
        const d = await safeJ(res);
        setCloneClasses(Array.isArray(d?.Classes) ? d.Classes : []);
      } catch (e) { console.error(e); }
    })();
  }, [cloneOpen, cloneForm.FromFinancialYearId, selectedBranchId]);

 const handleClone = () => {
 if (!cloneForm.ClassId || !cloneForm.SectionId || !cloneForm.SubjectId || !cloneForm.MonthId || !cloneForm.FromFinancialYearId || !cloneForm.ToFinancialYearId) {
    showSnack('Please select all fields', 'error');
    return;
  }
  if (cloneForm.FromFinancialYearId === cloneForm.ToFinancialYearId) {
    showSnack('Source and target financial year must be different', 'error');
    return;
  }
  setCloneConfirmOpen(true); // instead of calling the API here
};

const performClone = async () => {
  setCloneSaving(true);
  try {
    const body = {
      ClassId: cloneForm.ClassId, SectionId: cloneForm.SectionId || 0, SubjectId: cloneForm.SubjectId || 0,
      MonthId: cloneForm.MonthId || 0, FromFinancialYearId: cloneForm.FromFinancialYearId,
      ToFinancialYearId: cloneForm.ToFinancialYearId, CloneTopics: cloneForm.CloneTopics, ResetTracking: cloneForm.ResetTracking,
    };
    const res = await apiPost(`/CircularSyllabus/CloneToFinancialYear?Branch_id=${selectedBranchId}`, body);
    const d = await safeJ(res);
    if (res.ok && (d?.Success ?? true)) {
      showSnack(d?.Message || `Cloned ${d?.ClonedSyllabusCount ?? ''} syllabus record(s) successfully`);
      setCloneConfirmOpen(false);
      setCloneOpen(false);
      loadList();
    } else showSnack(d?.Message || 'Clone failed', 'error');
  } catch (e) {
    console.error(e);
    showSnack('Error cloning syllabus', 'error');
  } finally { setCloneSaving(false); }
}; 

  // Import Handler
  const handleDownloadTemplate = async () => {
    try {
      const res = await apiPost(`/CircularSyllabus/DownloadImportTemplate`, {});
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CircularSyllabus_ImportTemplate.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showSnack('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      showSnack('Please choose an Excel file first', 'error');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await apiPostForm(`/CircularSyllabus/ImportFromExcel?Branch_id=${selectedBranchId}`, fd);
      const d = await safeJ(res);
      if (res.ok) {
        setImportResult(d);
        showSnack(d?.Msg || 'Import completed');
        loadList();
      } else showSnack(d?.Msg || 'Import failed', 'error');
    } catch (e) {
      console.error(e);
      showSnack('Error importing file', 'error');
    } finally { setImporting(false); }
  };

const reportFilterBody = () => ({
    ClassId: fClass || 0,
    SectionId: fSection || 0,
    SubjectId: fSubject || 0,
    MonthId: fMonth || 0,
    FinancialYearId: fFy || 0,
    FromDate: null,
    ToDate: null
  });


const openReport = async () => {
    if (!selectedBranchId) return;
    setReportLoading(true);
    try {
      const res = await apiPost(`/CircularSyllabus/GetReport?Branch_id=${selectedBranchId}`, reportFilterBody());
      const d = await safeJ(res);
      setReportRows(Array.isArray(d?.report) ? d.report : Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load report', 'error');
    } finally { setReportLoading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiPost(`/CircularSyllabus/ExportReport?Branch_id=${selectedBranchId}`, reportFilterBody());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CircularSyllabus_Report.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      showSnack('Report exported successfully');
    } catch (e) {
      console.error(e);
      showSnack('Failed to export report', 'error');
    } finally { setExporting(false); }
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Render
  return (
    <Box sx={{ bgcolor: T.surface, minHeight: '100vh', pb: 6, fontFamily: FONT_BODY }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${T.ink} 0%, #1A3460 55%, #1e4d8c 100%)`,
        pt: 4, pb: 3, px: { xs: 2, md: 4 }, mb: 3, position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.25) 0%, rgba(37,99,235,0) 70%)',
          animation: 'floatA 9s ease-in-out infinite'
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -50, left: '28%', width: 180, height: 180, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0) 70%)',
          animation: 'floatB 11s ease-in-out infinite'
        },
        '@keyframes floatA': { '0%,100%': { transform: 'translate(0,0)' }, '50%': { transform: 'translate(-16px,14px)' } },
        '@keyframes floatB': { '0%,100%': { transform: 'translate(0,0)' }, '50%': { transform: 'translate(14px,-10px)' } },
      }}>
        <OrbitDecoration />
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2,
          position: 'relative', zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Zoom in timeout={500}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: '20px', p: 1.2, display: 'flex', alignItems: 'center', gap: 1.5
              }}>
                <RingProgress pct={0} size={0} label={false} />
                <Box sx={{ position: 'relative', width: 74, height: 74, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="74" height="74" style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
                    <defs>
                      <linearGradient id="heroRing" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#93C5FD" />
                        <stop offset="100%" stopColor="#C4B5FD" />
                      </linearGradient>
                    </defs>
                    <circle cx="37" cy="37" r="30" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" />
                    <circle
                      cx="37" cy="37" r="30" fill="none" stroke="url(#heroRing)" strokeWidth="7"
                      strokeDasharray={2 * Math.PI * 30}
                      strokeDashoffset={2 * Math.PI * 30 * (1 - Math.min(100, kpis.avgCoverage) / 100)}
                      strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }}
                    />
                  </svg>
                  <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', fontFamily: FONT_DISPLAY }}>
                    {Math.round(kpis.avgCoverage)}%
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Overall coverage
                  </Typography>
                  <Typography sx={{ color: '#fff', fontSize: '0.78rem', fontWeight: 600 }}>
                    {kpis.completed} of {kpis.totalSyllabus} themes complete
                  </Typography>
                </Box>
              </Box>
            </Zoom>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
                <Box sx={{
                  bgcolor: T.indigo, borderRadius: '10px', p: 0.9, display: 'flex',
                  boxShadow: '0 4px 14px rgba(37,99,235,0.45)'
                }}>
                  <SyllabusIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography variant="h5" sx={{
                  color: '#fff', fontWeight: 800, letterSpacing: -0.5, fontSize: '1.4rem', fontFamily: FONT_DISPLAY
                }}>
                  Manage Syllabus
                </Typography>
              </Box>
              <Typography variant="body2" sx={{
                color: 'rgba(255,255,255,0.5)', pl: 5.5, fontSize: '0.78rem'
              }}>
                Plan monthly topics · Track coverage · Clone across years
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, position: 'relative', zIndex: 1 }}>
  <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" justifyContent="flex-end">
    <KpiChip label="Syllabus" value={kpis.totalSyllabus} bg="rgba(37,99,235,0.15)" border="rgba(96,165,250,0.3)" color="#93C5FD" delay={0} />
    <KpiChip label="Completed" value={kpis.completed} bg="rgba(5,150,105,0.15)" border="rgba(52,211,153,0.3)" color="#6EE7B7" delay={120} />
    <KpiChip label="Avg Coverage" value={kpis.avgCoverage} suffix="%" bg="rgba(2,132,199,0.15)" border="rgba(125,211,252,0.3)" color="#7DD3FC" delay={240} />
    <KpiChip label="Pending" value={kpis.pendingTopics} bg="rgba(217,119,6,0.15)" border="rgba(251,191,36,0.3)" color="#FCD34D" delay={360} />
  </Stack>

  {isMaster && branches.length > 0 && (
    <Box sx={{
      bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px',
      px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1
    }}>
      <LocationOnIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
      <Select
        value={selectedBranchId || ''}
        onChange={e => setSelectedBranchId(Number(e.target.value))}
        variant="standard" disableUnderline
        sx={{
          color: '#fff', fontSize: '0.78rem', fontWeight: 600,
          '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)', fontSize: 16 },
          minWidth: 120
        }}
        MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: 1 } } }}
      >
        {branches.map(b => (
          <MenuItem key={b.branchId} value={b.branchId} sx={{ fontSize: '0.82rem' }}>
            {b.branchName}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )}
</Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
        <Fade in timeout={500}>
          <Card sx={{
            borderRadius: '18px',
            boxShadow: '0 4px 24px rgba(15,25,35,0.08)',
            border: `1px solid ${T.border}`,
            overflow: 'hidden'
          }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: `1px solid ${T.border}`, bgcolor: T.ghost }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem', fontFamily: FONT_BODY, textTransform: 'none' },
                  '& .Mui-selected': { color: T.indigo },
                  '& .MuiTabs-indicator': { bgcolor: T.indigo, height: 3, borderRadius: '3px 3px 0 0' }
                }}
              >
                <Tab label="Syllabus List" icon={<SyllabusIcon fontSize="small" />} iconPosition="start" />
                <Tab label="Analytics" icon={<BarChartIcon fontSize="small" />} iconPosition="start" />
                <Tab label="Coverage Report" icon={<TrackIcon fontSize="small" />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              {activeTab === 0 && (
                <>
                  {/* Filters */}
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.82rem' }}>Class</InputLabel>
                        <Select
                          label="Class" value={fClass} onChange={e => { setFClass(e.target.value); setPage(1); }}
                          sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
                        >
                          <MenuItem value=""><em>All Classes</em></MenuItem>
                          {classes.map(c => (
                            <MenuItem key={cId(c)} value={cId(c)} sx={{ fontSize: '0.82rem' }}>
                              {cName(c)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small" disabled={!fClass}>
                        <InputLabel sx={{ fontSize: '0.82rem' }}>Section</InputLabel>
                        <Select
                          label="Section" value={fSection} onChange={e => { setFSection(e.target.value); setPage(1); }}
                          sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
                        >
                          <MenuItem value=""><em>All Sections</em></MenuItem>
                          {filtefSections.map(s => (
                            <MenuItem key={secId(s)} value={secId(s)} sx={{ fontSize: '0.82rem' }}>
                              {secName(s)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small" disabled={!fClass}>
                        <InputLabel sx={{ fontSize: '0.82rem' }}>Subject</InputLabel>
                        <Select
                          label="Subject" value={fSubject} onChange={e => { setFSubject(e.target.value); setPage(1); }}
                          sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
                        >
                          <MenuItem value=""><em>All Subjects</em></MenuItem>
                          {filtefSubjects.map(s => (
                            <MenuItem key={subId(s)} value={subId(s)} sx={{ fontSize: '0.82rem' }}>
                              {subName(s)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.82rem' }}>Month</InputLabel>
                        <Select
                          label="Month" value={fMonth} onChange={e => { setFMonth(e.target.value); setPage(1); }}
                          sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
                        >
                          <MenuItem value=""><em>All Months</em></MenuItem>
                          {months.map(m => (
                            <MenuItem key={moId(m)} value={moId(m)} sx={{ fontSize: '0.82rem' }}>
                              {moName(m)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.82rem' }}>Financial Year</InputLabel>
                        <Select
                          label="Financial Year" value={fFy} onChange={e => { setFFy(e.target.value); setPage(1); }}
                          sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
                        >
                          {fys.map(f => (
                            <MenuItem key={fyId(f)} value={fyId(f)} sx={{ fontSize: '0.82rem' }}>
                              {fyName(f)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined" fullWidth startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                        onClick={loadList}
                        sx={{
                          borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, height: 40,
                          textTransform: 'none',
                          '&:hover': { borderColor: T.indigo, color: T.indigo, bgcolor: '#EFF6FF' }
                        }}
                      >
                        Reload
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
                    <SearchBar
                      value={search}
                      onChange={v => { setSearch(v); setPage(1); }}
                      placeholder="Search theme, class, subject…"
                      width={260}
                    />
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                     
                      <Button
                        variant="outlined" startIcon={<ImportIcon sx={{ fontSize: 15 }} />}
                        onClick={() => { setImportFile(null); setImportResult(null); setImportOpen(true); }}
                        sx={{
                          borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none',
                          '&:hover': { borderColor: T.teal, color: T.teal, bgcolor: '#F0FDFA' }
                        }}
                      >
                        Import
                      </Button>
                      <Button
                        variant="outlined" startIcon={<CloneIcon sx={{ fontSize: 15 }} />}
                        onClick={openClone}
                        sx={{
                          borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none',
                          '&:hover': { borderColor: T.sky, color: T.sky, bgcolor: T.skyL }
                        }}
                      >
                        Clone
                      </Button>
                      <Button
                        variant="contained" startIcon={<AddIcon sx={{ fontSize: 17 }} />}
                        onClick={openCreate}
                        sx={{
                          borderRadius: '10px', fontWeight: 700, bgcolor: T.indigo, textTransform: 'none',
                          boxShadow: '0 2px 10px rgba(37,99,235,0.35)',
                          '&:hover': { bgcolor: T.indigoD, boxShadow: '0 4px 14px rgba(37,99,235,0.45)' }
                        }}
                      >
                        Add Syllabus
                      </Button>
                    </Box>
                  </Box>

                  {/* Table */}
                  <TableContainer sx={{ maxHeight: 560, mt: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TH width={44}>#</TH>
                          <TH>Class / Section</TH>
                          <TH>Subject</TH>
                          <TH>Month</TH>
                          <TH>Theme</TH>
                          <TH width={110} align="center">Coverage</TH>
                          <TH align="center" width={120}>Status</TH>
                          <TH width={100}>Created</TH>
                          <TH align="center" width={130}>Actions</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />) :
                          pagedList.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} sx={{ py: 8, border: 0 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Box sx={{
                                    bgcolor: '#EFF6FF', borderRadius: '50%', width: 72, height: 72,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2
                                  }}>
                                    <SyllabusIcon sx={{ fontSize: 36, color: T.indigo }} />
                                  </Box>
                                  <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.5, fontFamily: FONT_DISPLAY }}>
                                    No syllabus found
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {list.length === 0 ? 'Get started by creating a monthly syllabus.' : 'No results match your filters.'}
                                  </Typography>
                                  {list.length === 0 && (
                                    <Button
                                      variant="contained" startIcon={<AddIcon />}
                                      onClick={openCreate}
                                      sx={{
                                        borderRadius: '10px', fontWeight: 700, bgcolor: T.indigo, textTransform: 'none',
                                        '&:hover': { bgcolor: T.indigoD }
                                      }}
                                    >
                                      Add Syllabus
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : pagedList.map((rec, idx) => {
                            const total = rec.totalTopics ?? 0;
                            const covered = rec.coveredTopics ?? 0;
                            const pct = rec.coveragePercentage ?? (total ? (covered / total) * 100 : 0);
                            const st = coverageState(pct);
                            const rowNum = (page - 1) * rows + idx + 1;
                            return (
                              <Fade in timeout={300 + idx * 60} key={rec.syllabusId}>
                                <TableRow hover sx={{
                                  '&:last-child td': { border: 0 },
                                  '&:hover': { bgcolor: '#F8FAFF' },
                                  transition: 'background .15s'
                                }}>
                                  <TableCell sx={{ color: T.faint, fontSize: '0.7rem' }}>{rowNum}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{
                                        width: 28, height: 28, bgcolor: '#DBEAFE', color: T.indigo,
                                        fontSize: '0.7rem', fontWeight: 800
                                      }}>
                                        {(rec.className ?? '?').charAt(0).toUpperCase()}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.8rem' }}>
                                          {rec.className ?? '—'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.66rem' }}>
                                          {rec.sectionName || 'All Sections'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                      {rec.subjectName ?? '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                      {rec.monthName ?? '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.8rem' }}>
                                      {rec.themeNo ? `${rec.themeNo}. ` : ''}{rec.themeName ?? '—'}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                      color: T.muted, fontSize: '0.68rem', display: 'block',
                                      maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }}>
                                      {rec.themeDescription || ''}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip title={`${covered} of ${total} topics covered`}>
                                      <Box>
                                        <RingProgress pct={pct} size={40} stroke={4.5} colorFrom={st.color} colorTo={RING_GRADIENT[1]} />
                                      </Box>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center">
                                    <StatusPill status={st.label} />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.7rem' }}>
                                      {fmt(rec.createdOn)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip title="Track topic coverage">
                                      <IconButton
                                        size="small" onClick={() => openTrack(rec)}
                                        sx={{
                                          color: T.teal, bgcolor: '#F0FDFA', width: 28, height: 28, mr: 0.6,
                                          '&:hover': { bgcolor: '#CCFBF1' }
                                        }}
                                      >
                                        <TrackIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small" onClick={() => openEdit(rec)}
                                        sx={{
                                          color: T.indigo, bgcolor: '#EFF6FF', width: 28, height: 28, mr: 0.6,
                                          '&:hover': { bgcolor: '#DBEAFE' }
                                        }}
                                      >
                                        <EditIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small" onClick={() => { setDeleteRec(rec); setDeleteOpen(true); }}
                                        sx={{
                                          color: T.rose, bgcolor: '#FFF1F2', width: 28, height: 28,
                                          '&:hover': { bgcolor: '#FFE4E6' }
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              </Fade>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Pager
                    total={filteredList.length}
                    page={page}
                    rowsPerPage={rows}
                    onPage={setPage}
                    onRows={setRows}
                  />
                </>
              )}

             {activeTab === 1 && (
  <Box sx={{ mt: 1 }}>
    <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.82rem' }}>Class</InputLabel>
          <Select
            label="Class" value={fClass} onChange={e => setFClass(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Classes</em></MenuItem>
            {classes.map(c => (
              <MenuItem key={cId(c)} value={cId(c)} sx={{ fontSize: '0.82rem' }}>{cName(c)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small" disabled={!fClass}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Section</InputLabel>
          <Select
            label="Section" value={fSection} onChange={e => setFSection(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Sections</em></MenuItem>
            {filtefSections.map(s => (
              <MenuItem key={secId(s)} value={secId(s)} sx={{ fontSize: '0.82rem' }}>{secName(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small" disabled={!fClass}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Subject</InputLabel>
          <Select
            label="Subject" value={fSubject} onChange={e => setFSubject(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Subjects</em></MenuItem>
            {filtefSubjects.map(s => (
              <MenuItem key={subId(s)} value={subId(s)} sx={{ fontSize: '0.82rem' }}>{subName(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.82rem' }}>Month</InputLabel>
          <Select
            label="Month" value={fMonth} onChange={e => setFMonth(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Months</em></MenuItem>
            {months.map(m => (
              <MenuItem key={moId(m)} value={moId(m)} sx={{ fontSize: '0.82rem' }}>{moName(m)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.82rem' }}>Financial Year</InputLabel>
          <Select
            label="Financial Year" value={fFy} onChange={e => setFFy(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            {fys.map(f => (
              <MenuItem key={fyId(f)} value={fyId(f)} sx={{ fontSize: '0.82rem' }}>{fyName(f)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <Button
          variant="outlined" fullWidth startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          onClick={loadAnalytics} disabled={analyticsLoading}
          sx={{
            borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, height: 40,
            textTransform: 'none',
            '&:hover': { borderColor: T.indigo, color: T.indigo, bgcolor: '#EFF6FF' }
          }}
        >
          Reload
        </Button>
      </Grid>
    </Grid>

    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <OverallCompletionCard  key={analyticsReloadKey} pct={analyticsKpis.avgCoverage} classCount={analyticsData.coverageByClass.length} />
      </Grid>
      <Grid item xs={12} md={4}>
        <CoverageByClassChart key={analyticsReloadKey} data={analyticsData.coverageByClass} />
      </Grid>
      <Grid item xs={12} md={4}>
        <StatusPieChart key={analyticsReloadKey} data={analyticsData.statusDistribution} />
      </Grid>
    </Grid>
  </Box>
)}

            {activeTab === 2 && (
  <Box sx={{ mt: 1 }}>
    <Grid container spacing={1.5} alignItems="center">
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.82rem' }}>Class</InputLabel>
          <Select
            label="Class" value={fClass} onChange={e => setFClass(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Classes</em></MenuItem>
            {classes.map(c => (
              <MenuItem key={cId(c)} value={cId(c)} sx={{ fontSize: '0.82rem' }}>{cName(c)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small" disabled={!fClass}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Section</InputLabel>
          <Select
            label="Section" value={fSection} onChange={e => setFSection(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Sections</em></MenuItem>
            {filtefSections.map(s => (
              <MenuItem key={secId(s)} value={secId(s)} sx={{ fontSize: '0.82rem' }}>{secName(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small" disabled={!fClass}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Subject</InputLabel>
          <Select
            label="Subject" value={fSubject} onChange={e => setFSubject(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Subjects</em></MenuItem>
            {filtefSubjects.map(s => (
              <MenuItem key={subId(s)} value={subId(s)} sx={{ fontSize: '0.82rem' }}>{subName(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.82rem' }}>Month</InputLabel>
          <Select
            label="Month" value={fMonth} onChange={e => setFMonth(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            <MenuItem value=""><em>All Months</em></MenuItem>
            {months.map(m => (
              <MenuItem key={moId(m)} value={moId(m)} sx={{ fontSize: '0.82rem' }}>{moName(m)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.82rem' }}>Financial Year</InputLabel>
          <Select
            label="Financial Year" value={fFy} onChange={e => setFFy(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: T.card, fontSize: '0.82rem' }}
          >
            {fys.map(f => (
              <MenuItem key={fyId(f)} value={fyId(f)} sx={{ fontSize: '0.82rem' }}>{fyName(f)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" fullWidth startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
            onClick={openReport}
            sx={{
              borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, height: 40,
              textTransform: 'none',
              '&:hover': { borderColor: T.indigo, color: T.indigo, bgcolor: '#EFF6FF' }
            }}
          >
            Load
          </Button>
          <Button
            variant="contained" fullWidth
            startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon sx={{ fontSize: 15 }} />}
            onClick={handleExport} disabled={exporting}
            sx={{
              borderRadius: '10px', fontWeight: 700, bgcolor: T.violet, textTransform: 'none', height: 40,
              '&:hover': { bgcolor: '#6D28D9' },
              '&:disabled': { opacity: 0.6 }
            }}
          >
            Export
          </Button>
        </Box>
      </Grid>
    </Grid>

    <TableContainer sx={{ maxHeight: 560, mt: 2 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TH>Class / Subject</TH>
            <TH>Theme</TH>
            <TH>Topic</TH>
            <TH align="center">Status</TH>
            <TH>Modified By</TH>
            <TH>Modified Date</TH>
          </TableRow>
        </TableHead>
        <TableBody>
          {reportLoading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
          ) : reportRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 6, color: T.muted }}>
                No report data yet — click "Load" above.
              </TableCell>
            </TableRow>
          ) : reportRows.map((r, i) => (
            <Fade in timeout={200 + i * 60} key={i}>
              <TableRow hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                    {r.className} {r.sectionName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.66rem' }}>
                    {r.subjectName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{r.themeName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{r.topicName}</Typography>
                </TableCell>
                <TableCell align="center">
                  <StatusPill status={STATUS_MAP[r.status] ?? r.status} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                    {r.coveredByTeacherName ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                    {r.coveredDate ?? '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            </Fade>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)}
            </CardContent>
          </Card>
        </Fade>
      </Container>

      {/* Add/Edit Dialog */}
      <Dialog
        open={formOpen}
        onClose={() => !formSaving && setFormOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${T.ink} 0%, #1A3460 60%, #1e4d8c 100%)`,
          color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ bgcolor: T.indigo, borderRadius: '8px', p: 0.6, display: 'flex' }}>
              {formMode === 'create' ? <AddIcon sx={{ fontSize: 18, color: '#fff' }} /> : <EditIcon sx={{ fontSize: 18, color: '#fff' }} />}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', fontFamily: FONT_DISPLAY }}>
              {formMode === 'create' ? 'Add Syllabus' : 'Edit Syllabus'}
            </Typography>
          </Box>
          <IconButton
            size="small" onClick={() => setFormOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3 }}>
          {formLoadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: T.indigo }} />
            </Box>
          ) : (
            <>
              {formMode === 'edit' ? (
                <Box sx={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, p: 2, mb: 2.5,
                  borderRadius: '12px', bgcolor: T.ghost, border: `1px solid ${T.border}`
                }}>
                  {[
                    { label: 'Class / Section', value: `${form.className ?? ''} ${form.sectionName ? '· ' + form.sectionName : ''}` },
                    { label: 'Subject', value: form.subjectName ?? '' },
                    { label: 'Month', value: form.MonthName ?? '' },
                    { label: 'Financial Year', value: form.FinancialYear ?? '' },
                  ].map((f, i) => (
                    <Box key={i}>
                      <Typography variant="caption" sx={{
                        color: T.muted, fontWeight: 600, fontSize: '0.64rem',
                        textTransform: 'uppercase', letterSpacing: 0.5
                      }}>
                        {f.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: T.ink, fontSize: '0.82rem' }}>
                        {f.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.82rem' }}>Class *</InputLabel>
                      <Select
                        label="Class *" value={form.ClassId}
                        onChange={e => setForm(f => ({ ...f, ClassId: e.target.value, SectionId: '' }))}
                        sx={{ borderRadius: '10px' }}
                      >
                        {classes.map(c => (
                          <MenuItem key={cId(c)} value={cId(c)}>{cName(c)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" disabled={!form.ClassId}>
                      <InputLabel sx={{ fontSize: '0.82rem' }}>Section</InputLabel>
                      <Select
                        label="Section" value={form.SectionId}
                        onChange={e => setForm(f => ({ ...f, SectionId: e.target.value }))}
                        sx={{ borderRadius: '10px' }}
                      >
                        <MenuItem value=""><em>All Sections</em></MenuItem>
                        {formSections.map(s => (
                          <MenuItem key={secId(s)} value={secId(s)}>{secName(s)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" disabled={!form.ClassId}>
                      <InputLabel sx={{ fontSize: '0.82rem' }}>Subject *</InputLabel>
                      <Select
                        label="Subject *" value={form.SubjectId}
                        onChange={e => setForm(f => ({ ...f, SubjectId: e.target.value }))}
                        sx={{ borderRadius: '10px' }}
                      >
                        {formSubjects.map(s => (
                          <MenuItem key={subId(s)} value={subId(s)}>{subName(s)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.82rem' }}>Month *</InputLabel>
                      <Select
                        label="Month *" value={form.MonthId}
                        onChange={e => setForm(f => ({ ...f, MonthId: e.target.value }))}
                        sx={{ borderRadius: '10px' }}
                      >
                        {months.map(m => (
                          <MenuItem key={moId(m)} value={moId(m)}>{moName(m)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.82rem' }}>Financial Year *</InputLabel>
                      <Select
                        label="Financial Year *" value={form.FinancialYearId}
                        onChange={e => setForm(f => ({ ...f, FinancialYearId: e.target.value }))}
                        sx={{ borderRadius: '10px' }}
                      >
                        {fys.map(f => (
                          <MenuItem key={fyId(f)} value={fyId(f)}>{fyName(f)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                <Grid item xs={3}>
                  <TextField
                    label="Theme No" type="number" size="small" fullWidth
                    value={form.ThemeNo} onChange={e => setForm(f => ({ ...f, ThemeNo: e.target.value }))}
                    inputProps={{ min: 0 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    label="Theme Name *" size="small" fullWidth
                    value={form.themeName} onChange={e => setForm(f => ({ ...f, themeName: e.target.value }))}
                    placeholder="e.g. Listening Skills"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Theme Description" size="small" fullWidth multiline rows={2}
                    value={form.themeDescription} onChange={e => setForm(f => ({ ...f, themeDescription: e.target.value }))}
                    placeholder="Short description of this month's theme"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '0.85rem' }}>Topics</Typography>
                <Button
                  size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={addTopicRow}
                  sx={{
                    borderRadius: '8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none',
                    color: T.indigo, bgcolor: '#EFF6FF',
                    '&:hover': { bgcolor: '#DBEAFE' }
                  }}
                >
                  Add Topic
                </Button>
              </Box>
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: 1,
                maxHeight: 260, overflowY: 'auto', pr: 0.5
              }}>
                {form.Topics.map((t, i) => (
                  <Grow in timeout={200} key={t._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 26, height: 26, borderRadius: '8px', bgcolor: T.ghost,
                        border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800,
                        color: T.muted, flexShrink: 0
                      }}>
                        {i + 1}
                      </Box>
                      <TextField
                        size="small" fullWidth value={t.TopicName}
                        placeholder={`Topic ${i + 1} name`}
                        onChange={e => updateTopicName(t._id, e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '9px', fontSize: '0.82rem' } }}
                      />
                      <IconButton
                        size="small" onClick={() => removeTopicRow(t._id)}
                        sx={{ color: T.rose, flexShrink: 0 }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Grow>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{
          p: '16px 24px', gap: 1.5, bgcolor: T.ghost,
          borderTop: `1px solid ${T.border}`
        }}>
          <Button
            onClick={() => setFormOpen(false)} variant="outlined"
            sx={{ flex: 1, borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveForm} variant="contained" disabled={formSaving}
            startIcon={formSaving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
            sx={{
              flex: 1, borderRadius: '10px', fontWeight: 700, bgcolor: T.indigo, textTransform: 'none',
              boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
              '&:hover': { bgcolor: T.indigoD },
              '&:disabled': { opacity: 0.6 }
            }}
          >
            {formSaving ? 'Saving…' : formMode === 'create' ? 'Create Syllabus' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Topic Tracking Dialog (quick edit) */}
      <Dialog
        open={trackOpen}
        onClose={() => !trackSaving && setTrackOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, #0c3251 0%, #0d5c52 60%, #0D9488 100%)`,
          color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ bgcolor: T.teal, borderRadius: '8px', p: 0.6, display: 'flex' }}>
              <TrackIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', fontFamily: FONT_DISPLAY }}>Topic Coverage</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)' }}>
                {trackSyllabus?.themeName} · {trackSyllabus?.className} {trackSyllabus?.sectionName}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small" onClick={() => setTrackOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3 }}>
          {trackLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: T.teal }} />
            </Box>
          ) : (
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 1.2,
              maxHeight: 440, overflowY: 'auto', pr: 0.5
            }}>
              {trackTopics.map((t, i) => {
                const isExpanded = expandedRemarks[t.TrackingId] || false;
                return (
                  <Fade in timeout={200 + i * 60} key={t.TrackingId}>
                    <Box sx={{
                      p: 1.5, borderRadius: '12px',
                      border: `1.5px solid ${t._dirty ? '#99F6E4' : T.border}`,
                      bgcolor: t._dirty ? '#F0FDFA' : T.card,
                      transition: 'all .2s'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '6px', bgcolor: T.ghost,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.68rem', fontWeight: 800, color: T.muted, flexShrink: 0
                        }}>
                          {t.TopicSequence}
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '0.83rem', flex: 1 }}>
                          {t.TopicName}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 128 }}>
                          <Select
                            value={t.Status}
                            onChange={e => updateTrackTopic(t.TrackingId, { Status: e.target.value })}
                            sx={{ borderRadius: '8px', fontSize: '0.75rem', height: 30 }}
                          >
                            <MenuItem value="P" sx={{ fontSize: '0.8rem' }}>Pending</MenuItem>
                            <MenuItem value="IP" sx={{ fontSize: '0.8rem' }}>In Progress</MenuItem>
                            <MenuItem value="C" sx={{ fontSize: '0.8rem' }}>Covered</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <TextField
                        size="small" fullWidth placeholder="Add new remarks (optional)"
                        value={t.Description}
                        onChange={e => updateTrackTopic(t.TrackingId, { Description: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.78rem', bgcolor: T.ghost } }}
                      />

                      {t.CoveredDescription && t.CoveredDescription !== t.Description && (
                        <>
                          <Button
                            size="small"
                            onClick={() => setExpandedRemarks(prev => ({ ...prev, [t.TrackingId]: !prev[t.TrackingId] }))}
                            sx={{ mt: 1, color: T.indigo, fontWeight: 600, fontSize: '0.7rem', textTransform: 'none', p: 0, justifyContent: 'flex-start' }}
                          >
                            {isExpanded ? 'Hide Old Remarks' : 'Show Old Remarks'}
                          </Button>
                          {isExpanded && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#F8FAFC', borderRadius: '8px', border: `1px solid ${T.border}` }}>
                              <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.7rem', lineHeight: 1.4 }}>
                                {t.CoveredDescription}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}

                      {t.CoveredByTeacherName && (
                        <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
                          Last updated by {t.CoveredByTeacherName}{t.CoveredDate ? ` · ${t.CoveredDate}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          p: '16px 24px', gap: 1.5, bgcolor: T.ghost,
          borderTop: `1px solid ${T.border}`
        }}>
          <Button
            onClick={() => setTrackOpen(false)} variant="outlined"
            sx={{ flex: 1, borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTrack} variant="contained" disabled={trackSaving}
            startIcon={trackSaving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
            sx={{
              flex: 1, borderRadius: '10px', fontWeight: 700, bgcolor: T.teal, textTransform: 'none',
              '&:hover': { bgcolor: '#0B7A70' },
              '&:disabled': { opacity: 0.6 }
            }}
          >
            {trackSaving ? 'Saving…' : 'Save Coverage'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog
        open={cloneOpen}
        onClose={() => !cloneSaving && setCloneOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, #0c3251 0%, #0369A1 60%, #0284C7 100%)`,
          color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ bgcolor: T.sky, borderRadius: '8px', p: 0.6, display: 'flex' }}>
              <CloneIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', fontFamily: FONT_DISPLAY }}>Clone Syllabus to New Year</Typography>
          </Box>
          <IconButton
            size="small" onClick={() => setCloneOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3 }}>
          <Grid container spacing={1.5} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.82rem' }}>From Financial Year *</InputLabel>
                <Select
                  label="From Financial Year *" value={cloneForm.FromFinancialYearId}
                  onChange={e => setCloneForm(f => ({ ...f, FromFinancialYearId: e.target.value }))}
                  sx={{ borderRadius: '10px' }}
                >
                  {fys.map(f => (
                    <MenuItem key={fyId(f)} value={fyId(f)}>{fyName(f)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
           <Grid item xs={6}>
  <FormControl fullWidth size="small" error={cloneForm.ToFinancialYearId && cloneForm.ToFinancialYearId === cloneForm.FromFinancialYearId}>
    <InputLabel sx={{ fontSize: '0.82rem' }}>To Financial Year *</InputLabel>
    <Select
      label="To Financial Year *" value={cloneForm.ToFinancialYearId}
      onChange={e => setCloneForm(f => ({ ...f, ToFinancialYearId: e.target.value }))}
      sx={{ borderRadius: '10px' }}
    >
      {fys
        .filter(f => fyId(f) !== cloneForm.FromFinancialYearId)
        .map(f => (
          <MenuItem key={fyId(f)} value={fyId(f)}>{fyName(f)}</MenuItem>
        ))}
    </Select>
    {cloneForm.ToFinancialYearId && cloneForm.ToFinancialYearId === cloneForm.FromFinancialYearId && (
      <Typography variant="caption" sx={{ color: T.rose, mt: 0.5, ml: 0.5 }}>
        Must differ from From Year
      </Typography>
    )}
  </FormControl>
</Grid>
          </Grid>

          {cloneForm.FromFinancialYearId && cloneClasses.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {cloneClasses.map(c => (
                <Chip
                  key={c.classId} label={`${c.className} · ${c.syllabusCount}`}
                  size="small" onClick={() => setCloneForm(f => ({ ...f, ClassId: c.classId }))}
                  sx={{
                    borderRadius: '8px', fontWeight: 600, fontSize: '0.7rem',
                    bgcolor: cloneForm.ClassId === c.classId ? T.indigo : T.ghost,
                    color: cloneForm.ClassId === c.classId ? '#fff' : T.slate,
                    border: `1px solid ${cloneForm.ClassId === c.classId ? T.indigo : T.border}`,
                    cursor: 'pointer', transition: 'all .15s'
                  }}
                />
              ))}
            </Box>
          )}

         <Grid container spacing={1.5}>
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.82rem' }}>Class *</InputLabel>
                <Select
                  label="Class *" value={cloneForm.ClassId}
                  onChange={e => setCloneForm(f => ({ ...f, ClassId: e.target.value, SectionId: '' }))}
                  sx={{ borderRadius: '10px' }}
                >
                  {classes.map(c => (
                    <MenuItem key={cId(c)} value={cId(c)}>{cName(c)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth size="small" disabled={!cloneForm.ClassId}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>Section *</InputLabel>
                <Select
                  label="Section" value={cloneForm.SectionId}
                  onChange={e => setCloneForm(f => ({ ...f, SectionId: e.target.value }))}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>All Sections</em></MenuItem>
                  {cloneSections.map(s => (
                    <MenuItem key={secId(s)} value={secId(s)}>{secName(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth size="small" disabled={!cloneForm.ClassId}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>Subject *</InputLabel>
                <Select
                  label="Subject" value={cloneForm.SubjectId}
                  onChange={e => setCloneForm(f => ({ ...f, SubjectId: e.target.value }))}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>All Subjects</em></MenuItem>
                  {cloneSubjects.map(s => (
                    <MenuItem key={subId(s)} value={subId(s)}>{subName(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.82rem' }}>Month *</InputLabel>
                <Select
                  label="Month" value={cloneForm.MonthId}
                  onChange={e => setCloneForm(f => ({ ...f, MonthId: e.target.value }))}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>All Months</em></MenuItem>
                  {months.map(m => (
                    <MenuItem key={moId(m)} value={moId(m)}>{moName(m)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{
            mt: 2, p: 1.5, borderRadius: '10px', bgcolor: T.skyL,
            border: '1px solid #BAE6FD'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small" checked={cloneForm.CloneTopics}
                  onChange={e => setCloneForm(f => ({ ...f, CloneTopics: e.target.checked }))}
                  sx={{ color: T.sky, '&.Mui-checked': { color: T.sky } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.slate }}>Copy topic tracking rows</Typography>}
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  size="small" checked={cloneForm.ResetTracking}
                  onChange={e => setCloneForm(f => ({ ...f, ResetTracking: e.target.checked }))}
                  sx={{ color: T.sky, '&.Mui-checked': { color: T.sky } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.slate }}>Reset all statuses to Pending</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: '16px 24px', gap: 1.5, bgcolor: T.ghost,
          borderTop: `1px solid ${T.border}`
        }}>
          <Button
            onClick={() => setCloneOpen(false)} variant="outlined"
            sx={{ flex: 1, borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClone} variant="contained" disabled={cloneSaving}
            startIcon={cloneSaving ? <CircularProgress size={14} color="inherit" /> : <SwapIcon />}
            sx={{
              flex: 1, borderRadius: '10px', fontWeight: 700, bgcolor: T.sky, textTransform: 'none',
              '&:hover': { bgcolor: '#0369A1' },
              '&:disabled': { opacity: 0.6 }
            }}
          >
            {cloneSaving ? 'Cloning…' : 'Clone Syllabus'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importOpen}
        onClose={() => !importing && setImportOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, #0c3251 0%, #0d5c52 60%, #0D9488 100%)`,
          color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ bgcolor: T.teal, borderRadius: '8px', p: 0.6, display: 'flex' }}>
              <ImportIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', fontFamily: FONT_DISPLAY }}>Bulk Import</Typography>
          </Box>
          <IconButton
            size="small" onClick={() => setImportOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3 }}>
            <Box sx={{
  mb: 2.5, p: 1.75, borderRadius: '12px', bgcolor: T.skyL, border: '1px solid #BAE6FD'
}}>
  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: T.ink, mb: 0.8 }}>
    How to use this template:
  </Typography>
  <Box component="ol" sx={{ m: 0, pl: 2.2, '& li': { fontSize: '0.72rem', color: T.slate, mb: 0.4, lineHeight: 1.4 } }}>
    <li>Use exact Class Name as shown in your school system (e.g., 'Nursery', 'Class 1', 'KG')</li>
    <li>Section Name is optional — leave blank if not applicable</li>
    <li>Use exact Subject Name (e.g., 'English', 'Hindi', 'Maths', 'EVS')</li>
    <li>Use full Month Name (e.g., 'January', 'February')</li>
    <li>Financial Year format: '2025-2026'</li>
    <li>Theme No: 1, 2, 3, etc. (numeric)</li>
    <li>Topic Sequence: 1, 2, 3, etc. (numeric)</li>
    <li>Each row = one topic. Multiple rows with same Theme = multiple topics under one theme</li>
    <li>Do NOT delete the header row</li>
    <li>Save file before uploading</li>
  </Box>
</Box>
          <Button
            fullWidth variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleDownloadTemplate}
            sx={{
              borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, mb: 2.5, py: 1, textTransform: 'none',
              '&:hover': { borderColor: T.teal, color: T.teal, bgcolor: '#F0FDFA' }
            }}
          >
            Download Import Template
          </Button>
          <Box component="label" sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
            border: `2px dashed ${importFile ? T.teal : T.border}`, borderRadius: '14px', p: 3, cursor: 'pointer',
            bgcolor: importFile ? '#F0FDFA' : T.ghost, transition: 'all .2s',
            '&:hover': { borderColor: T.teal, bgcolor: '#F0FDFA' },
          }}>
            <CloudUploadIcon sx={{ fontSize: 34, color: importFile ? T.teal : T.faint }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: T.ink, textAlign: 'center' }}>
              {importFile ? importFile.name : 'Click to choose an Excel file'}
            </Typography>
            <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.7rem' }}> .xlsx format only</Typography>
            <input type="file" accept=".xlsx" hidden onChange={e => setImportFile(e.target.files?.[0] ?? null)} />
          </Box>
          {importResult && (
            <Fade in>
              <Box sx={{
                mt: 2, p: 1.5, borderRadius: '10px', bgcolor: '#D1FAE5',
                border: '1px solid #6EE7B7', display: 'flex', alignItems: 'center', gap: 1
              }}>
                <CheckIcon sx={{ color: T.emerald, fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: T.emerald, fontSize: '0.8rem' }}>
                  Imported {importResult.TotalSyllabus ?? importResult.totalSyllabus ?? 0} syllabus, {importResult.TotalTopics ?? importResult.totalTopics ?? 0} topics
                </Typography>
              </Box>
            </Fade>
          )}
        </DialogContent>
        <DialogActions sx={{
          p: '16px 24px', gap: 1.5, bgcolor: T.ghost,
          borderTop: `1px solid ${T.border}`
        }}>
          <Button
            onClick={() => setImportOpen(false)} variant="outlined"
            sx={{ flex: 1, borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            onClick={handleImport} variant="contained" disabled={importing || !importFile}
            startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <ImportIcon />}
            sx={{
              flex: 1, borderRadius: '10px', fontWeight: 700, bgcolor: T.teal, textTransform: 'none',
              '&:hover': { bgcolor: '#0B7A70' },
              '&:disabled': { opacity: 0.6 }
            }}
          >
            {importing ? 'Uploading…' : 'Upload & Import'}
          </Button>
        </DialogActions>
      </Dialog>

    

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{
          bgcolor: T.rose, color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', gap: 1.2
        }}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '8px', p: 0.6, display: 'flex' }}>
            <WarnIcon sx={{ fontSize: 18 }} />
          </Box>
          Delete Syllabus
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3 }}>
          <Typography sx={{ mb: 1, color: T.muted, fontSize: '0.88rem' }}>
            Delete the syllabus <Box component="span" sx={{ fontWeight: 800, color: T.ink }}>{deleteRec?.themeName}</Box> for
            <Box component="span" sx={{ fontWeight: 800, color: T.ink }}> {deleteRec?.className} · {deleteRec?.subjectName}</Box>?
          </Typography>
          <Typography variant="caption" sx={{ color: T.faint }}>Only syllabus with all pending topics can be deleted.</Typography>
        </DialogContent>
        <DialogActions sx={{
          p: '16px 20px', gap: 1.5, bgcolor: T.ghost,
          borderTop: `1px solid ${T.border}`
        }}>
          <Button
            onClick={() => setDeleteOpen(false)} variant="outlined"
            sx={{ borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, textTransform: 'none' }}
          >
            Go Back
          </Button>
          <Button
            onClick={handleDelete} variant="contained" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{
              borderRadius: '10px', fontWeight: 700, bgcolor: T.rose, textTransform: 'none',
              '&:hover': { bgcolor: '#BE123C' }
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{
            width: '100%', fontWeight: 600, borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>

      <Dialog
  open={cloneConfirmOpen}
  onClose={() => !cloneSaving && setCloneConfirmOpen(false)}
  maxWidth="xs"
  fullWidth
   slotProps={{
    backdrop: {
      sx: {
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        backgroundColor: "rgba(0, 0, 0, 0.35)",
      },
    },
  }}
>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <WarnIcon fontSize="small" color="warning" /> Confirm Clone
  </DialogTitle>
  <DialogContent>
    <Typography sx={{ mb: 1 }}>
      Clone syllabus from{' '}
      <b>{fys.find(f => fyId(f) === cloneForm.FromFinancialYearId) ? fyName(fys.find(f => fyId(f) === cloneForm.FromFinancialYearId)) : ''}</b>{' '}
      to{' '}
      <b>{fys.find(f => fyId(f) === cloneForm.ToFinancialYearId) ? fyName(fys.find(f => fyId(f) === cloneForm.ToFinancialYearId)) : ''}</b>
      {' '}for{' '}
      <b>{classes.find(c => cId(c) === cloneForm.ClassId) ? cName(classes.find(c => cId(c) === cloneForm.ClassId)) : ''}</b>?
    </Typography>
    {cloneForm.ResetTracking && (
      <Typography variant="caption" color="error">
        This will reset all cloned topic statuses to Pending.
      </Typography>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setCloneConfirmOpen(false)} variant="outlined" disabled={cloneSaving}>
      Cancel
    </Button>
    <Button
      onClick={performClone}
      variant="contained"
      color="warning"
      disabled={cloneSaving}
      startIcon={cloneSaving ? <CircularProgress size={14} color="inherit" /> : <SwapIcon />}
    >
      {cloneSaving ? 'Cloning…' : 'Yes, Clone'}
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  );
}